import { Button, ButtonProps, ConfigProvider, theme } from 'antd';
import { ThemeConfig } from 'antd/lib';
import clsx from 'clsx';
import { ReactNode, useMemo } from 'react';
import styles from './custom-button.module.css';

const { useToken } = theme;

type CustomType =
  | NonNullable<ButtonProps['type']>
  | 'primaryLight'
  | 'secondary'
  | 'secondaryLight'
  | 'textBg'
  | 'darkBlue'
  | 'textBgLight';

interface Props extends Omit<ButtonProps, 'type'> {
  children?: ReactNode;
  type?: CustomType;
  fontWeight?: number;
  fontSize?: number;
}

const CustomButton = ({ children, type, fontWeight, fontSize, className, ...buttonProps }: Props): JSX.Element => {
  const { token } = useToken();

  const buttonThemes = useMemo(() => {
    return {
      primary: {
        Button: {
          colorPrimary: '#47B731',
          colorTextLightSolid: '#fff',
          colorPrimaryHover: '#338B22',
          colorPrimaryActive: '#47B731',
          colorBgContainerDisabled: '#47B731',
        },
      },
      primaryLight: {
        Button: {
          colorPrimary: '#bde7b4',
          colorTextLightSolid: token.colorText,
        },
      },
      secondary: {
        Button: {
          colorPrimary: '#6C757D',
          colorPrimaryHover: '#5C636A',
          colorPrimaryActive: '#565E64',
          colorBgContainerDisabled: '#6C757D',
          colorTextDisabled: '#FFFFFF',
        },
      },
      secondaryLight: {
        Button: {
          colorPrimary: '#CED4DA',
          colorPrimaryHover: '#b4babe',
          colorPrimaryActive: '#b4babe',
          colorBgContainerDisabled: '#CED4DA',
          colorTextDisabled: '#FFFFFF',
        },
      },
      textBg: {
        Button: {
          colorBorder: '#314F61',
          colorBgContainer: 'none',
          colorPrimary: '#47B731',
          colorBgTextHover: 'none',
          colorPrimaryHover: 'none',
        },
      },
      darkBlue: {
        Button: {
          colorPrimary: '#314F61',
          colorPrimaryHover: '#225574',
          colorPrimaryActive: '#14364A',
          colorBgContainerDisabled: '#6C757D',
          colorTextDisabled: '#FFFFFF',
        },
      },
      textBgLight: {
        Button: {
          colorBorder: '#bde7b4',
          colorBgContainer: 'none',
          colorPrimary: '#bde7b4',
        },
      },
    } as Record<CustomType, ThemeConfig['components']>;
  }, [token.colorText]);

  const buttonTheme = type ? buttonThemes[type] : buttonThemes['primary'];

  const getButtonType = (buttonType: Props['type']): ButtonProps['type'] => {
    if (!buttonType) {
      return 'primary';
    }

    const buttonTypes: Record<CustomType, ButtonProps['type']> = {
      textBg: 'default',
      primaryLight: 'primary',
      darkBlue: 'primary',
      secondary: 'primary',
      secondaryLight: 'primary',
      primary: 'primary',
      default: 'default',
      dashed: 'dashed',
      link: 'link',
      text: 'text',
      textBgLight: 'primary',
    };

    return buttonTypes[buttonType];
  };

  const buttonStyle = useMemo(() => {
    switch (type) {
      case buttonThemes['primary']:
        return styles.primary;
      case buttonThemes['secondary']:
        return styles.secondary;
      case buttonThemes['textBg']:
        return styles.textBg;
    }
  }, [buttonThemes, type]);

  const isDisabledStyle = useMemo(() => {
    if (buttonProps.disabled) {
      return styles.disabled;
    }
  }, [buttonProps.disabled]);

  return (
    <ConfigProvider
      theme={{
        token: {
          fontSize: fontSize || 16,
        },
        components: {
          ...buttonTheme,
        },
      }}
    >
      <Button
        type={getButtonType(type)}
        className={clsx(className, buttonStyle, styles.button, isDisabledStyle)}
        {...buttonProps}
      >
        <span
          style={{
            fontWeight: fontWeight || 600,
          }}
        >
          {children}
        </span>
      </Button>
    </ConfigProvider>
  );
};

export default CustomButton;
